import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { syncUtilityToZohoURL, getUtilityZohoSyncStatusURL } from "../../../utils/urls";

// The backend only allows this sync for Northlands.
export const ZOHO_UTILITY_SYNC_FACILITY_ID = "68fb27e876ed53c0e2af1742";

const POLL_INTERVAL_MS = 4000;

const MODES = [
  { value: "transactions", label: "Transactions", hint: "Each bank (NCBA) payment becomes an invoice with a payment against it." },
  { value: "debits_and_credits", label: "Debits & Credits", hint: "Per account and month: usage becomes the invoice, top-ups are applied as payment." },
];

const LABELS = { power: "Power", water: "Water" };
const ICONS = { power: "ti-bolt", water: "ti-droplet" };
const COLORS = { power: "#f59f00", water: "#1c7ed6" };

const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const money = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const STATUS = {
  dry_run: { label: "Ready to sync", cls: "bg-info-subtle text-info" },
  created: { label: "Synced", cls: "bg-success-subtle text-success" },
  already_synced: { label: "Already in Zoho", cls: "bg-secondary-subtle text-secondary" },
  failed: { label: "Failed", cls: "bg-danger-subtle text-danger" },
};

const CREDIT_ONLY_REASON = "Top-ups with no usage this month, so there is no invoice to create.";
const UNMATCHED_REASON = "Credited to this utility, but its account no longer exists.";

const emptyResult = { items: [], notCredited: [], unmatched: [], creditOnly: [], totals: null };

const toResult = (summary) => ({
  items: summary?.items || [],
  notCredited: summary?.notCredited || [],
  unmatched: summary?.unmatched || [],
  creditOnly: summary?.creditOnly || [],
  diagnostics: summary?.diagnostics || [],
  zohoCheck: summary?.zohoCheck || null,
  totals: summary?.totals || null,
});

const Stat = ({ label, value, tone }) => (
  <div className="flex-fill px-3 py-2 rounded-3" style={{ background: "var(--bs-light, #f6f7f9)", minWidth: 110 }}>
    <div className="text-muted small">{label}</div>
    <div className={`fw-semibold fs-5 ${tone || ""}`}>{value}</div>
  </div>
);

const ZohoUtilitySync = ({ facilityId, utility }) => {
  const label = LABELS[utility];
  // Power is synced from transactions only; water can also use debits & credits.
  const modes = utility === "power" ? MODES.filter((m) => m.value === "transactions") : MODES;

  const [mode, setMode] = useState("transactions");
  const [fromMonth, setFromMonth] = useState(lastMonth());
  const [toMonth, setToMonth] = useState(lastMonth());
  const [accountNumber, setAccountNumber] = useState("");

  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [isPreview, setIsPreview] = useState(true);
  const [result, setResult] = useState(emptyResult);
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [rowBusy, setRowBusy] = useState({});
  const pollRef = useRef(null);

  const running = job?.status === "running";

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const loadStatus = async () => {
    try {
      const res = await makeRequest2(`${getUtilityZohoSyncStatusURL(facilityId)}?utility=${utility}`, "GET");
      if (res?.success && res.data?.success) {
        const j = res.data.data;
        setJob(j);
        if (j.status !== "running") {
          stopPolling();
          if (j.summary) setResult(toResult(j.summary));
        }
        return j;
      }
    } catch (e) {
      /* no job yet */
    }
    return null;
  };

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(loadStatus, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    loadStatus().then((j) => j?.status === "running" && startPolling());
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, utility]);

  const request = (extra) => ({
    mode,
    utility,
    fromMonth,
    toMonth,
    accountNumber: accountNumber.trim() || undefined,
    ...extra,
  });

  const validRange = () => {
    if (!fromMonth || !toMonth || fromMonth > toMonth) {
      toastify("error", "Choose a valid month range");
      return false;
    }
    return true;
  };

  const preview = async () => {
    if (!validRange()) return;
    setBusy(true);
    try {
      const res = await makeRequest2(syncUtilityToZohoURL(facilityId), "POST", request({ dryRun: true }));
      if (res?.success && res.data?.success) {
        setResult(toResult(res.data.data));
        setIsPreview(true);
        setTab("pending");
        setSearch("");
        setOpen(true);
      } else {
        toastify("error", res?.data?.error || res?.error || "Preview failed");
      }
    } catch (e) {
      toastify("error", e?.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const syncAll = async () => {
    const pending = result.items.filter((i) => i.status === "dry_run" || i.status === "failed").length;
    if (!pending) return;
    if (!window.confirm(`Sync ${pending} ${label.toLowerCase()} invoice(s) (${fromMonth} to ${toMonth}) to Zoho Books?`)) return;
    setBusy(true);
    try {
      const res = await makeRequest2(syncUtilityToZohoURL(facilityId), "POST", request({ dryRun: false }));
      if (res?.success && res.data?.success) {
        toastify("success", "Sync started");
        setJob(res.data.data);
        setIsPreview(false);
        startPolling();
      } else {
        toastify("error", res?.data?.error || res?.error || "Sync failed to start");
      }
    } catch (e) {
      toastify("error", e?.message || "Sync failed to start");
    } finally {
      setBusy(false);
    }
  };

  // Sends a single invoice. Also used to re-sync an already synced row (the backend
  // only records what Zoho is missing) and to force a skipped water payment through.
  const syncOne = async (item, { force = false } = {}) => {
    const key = item.invoiceNumber;
    setRowBusy((b) => ({ ...b, [key]: true }));
    try {
      const res = await makeRequest2(
        syncUtilityToZohoURL(facilityId),
        "POST",
        request({ dryRun: false, wait: true, force, invoiceNumbers: [key], fromMonth: item.month, toMonth: item.month }),
      );
      if (res?.success && res.data?.success) {
        const updated = (res.data.data.items || []).find((i) => i.invoiceNumber === key);
        if (updated) {
          setResult((r) => {
            const exists = r.items.some((i) => i.invoiceNumber === key);
            return {
              ...r,
              items: exists ? r.items.map((i) => (i.invoiceNumber === key ? { ...i, ...updated } : i)) : [...r.items, updated],
              notCredited: r.notCredited.filter((n) => n.invoiceNumber !== key),
              unmatched: r.unmatched.filter((n) => n.invoiceNumber !== key),
            };
          });
          if (updated.status === "failed") toastify("error", updated.error || "Sync failed");
          else toastify("success", `${key} synced to Zoho`);
        } else {
          toastify("error", "Nothing was synced for this payment");
        }
      } else {
        toastify("error", res?.data?.error || res?.error || "Sync failed");
      }
    } catch (e) {
      toastify("error", e?.message || "Sync failed");
    } finally {
      setRowBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const groups = useMemo(() => {
    const items = result.items;
    return {
      pending: items.filter((i) => i.status === "dry_run"),
      synced: items.filter((i) => i.status === "created" || i.status === "already_synced"),
      failed: items.filter((i) => i.status === "failed"),
      skipped: [
        ...result.notCredited.map((n) => ({ kind: "Not credited", ref: n.transID, accountNumber: n.accountNumber, amount: n.amount, month: n.month, invoiceNumber: n.invoiceNumber, reason: n.reason || "The billing service rejected this payment, so no credit exists for it." })),
        ...result.unmatched.map((n) => ({ kind: "No account", ref: n.transID, accountNumber: n.accountNumber, amount: n.amount, month: n.month, invoiceNumber: n.invoiceNumber, reason: UNMATCHED_REASON })),
        ...result.creditOnly.map((n) => ({ kind: "Credit only", ref: n.month, accountNumber: n.accountNumber, amount: n.credited, reason: CREDIT_ONLY_REASON })),
      ],
    };
  }, [result]);

  const matches = (row) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [row.accountNumber, row.invoiceNumber, row.reference, row.ref, row.month, row.reason, row.kind].some((v) => String(v || "").toLowerCase().includes(q));
  };

  const visible = (groups[tab] || []).filter(matches);
  const pendingCount = groups.pending.length + groups.failed.length;
  const pendingAmount = [...groups.pending, ...groups.failed].reduce((s, i) => s + (i.amount || 0), 0);

  const tabs = [
    { id: "pending", label: "To sync", count: groups.pending.length },
    { id: "synced", label: "Synced", count: groups.synced.length },
    { id: "failed", label: "Failed", count: groups.failed.length, danger: true },
    { id: "skipped", label: "Skipped", count: groups.skipped.length, warn: true },
  ];

  const color = COLORS[utility];

  return (
    <>
      <div className="card h-100" style={{ borderTop: `3px solid ${color}` }}>
        <div className="card-body">
          <div className="d-flex align-items-center mb-3">
            <div className="d-flex align-items-center justify-content-center rounded-3 me-3" style={{ width: 44, height: 44, background: `${color}1f` }}>
              <i className={`ti ${ICONS[utility]}`} style={{ fontSize: 24, color }}></i>
            </div>
            <div className="flex-grow-1">
              <h5 className="mb-0">{label} → Zoho Books</h5>
              <small className="text-muted">Synced month by month. Safe to re-run, nothing is duplicated.</small>
            </div>
          </div>

          {modes.length > 1 ? (
            <div className="mb-3">
              <label className="form-label small text-muted mb-1">What to sync</label>
              <div className="btn-group w-100" role="group">
                {modes.map((m) => (
                  <button key={m.value} type="button" className={`btn btn-sm ${mode === m.value ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setMode(m.value)}>
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="text-muted small mt-1">{modes.find((m) => m.value === mode)?.hint}</div>
            </div>
          ) : (
            <div className="mb-3">
              <span className="badge bg-light text-dark border">Transactions</span>
              <span className="text-muted small ms-2">{modes[0].hint}</span>
            </div>
          )}

          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="form-label small text-muted mb-1">From month</label>
              <input type="month" className="form-control" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} />
            </div>
            <div className="col-6">
              <label className="form-label small text-muted mb-1">To month</label>
              <input type="month" className="form-control" value={toMonth} onChange={(e) => setToMonth(e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label small text-muted mb-1">Account number (optional)</label>
              <input type="text" className="form-control" placeholder="All accounts" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
            </div>
          </div>

          <button className="btn btn-primary w-100" disabled={busy || running} onClick={preview}>
            {busy ? (
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            ) : (
              <i className="ti ti-list-search me-2"></i>
            )}
            {running ? "Sync running..." : "Preview & sync"}
          </button>

          {job && (
            <div className="d-flex align-items-center justify-content-between mt-3 small">
              <span className="text-muted">
                <span className={`badge me-2 ${job.status === "failed" ? "bg-danger-subtle text-danger" : job.status === "running" ? "bg-warning-subtle text-warning" : "bg-success-subtle text-success"}`}>{job.status}</span>
                Last run {job.startedAt ? new Date(job.startedAt).toLocaleString() : ""}
              </span>
              {job.summary && (
                <button className="btn btn-link btn-sm p-0" onClick={() => { setResult(toResult(job.summary)); setIsPreview(false); setTab("synced"); setSearch(""); setOpen(true); }}>
                  View results
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog
        visible={open}
        onHide={() => setOpen(false)}
        header={
          <div className="d-flex align-items-center">
            <i className={`ti ${ICONS[utility]} me-2`} style={{ color }}></i>
            {label} → Zoho Books
            <span className="text-muted fs-6 ms-2">
              {fromMonth === toMonth ? fromMonth : `${fromMonth} to ${toMonth}`} · {mode === "transactions" ? "Transactions" : "Debits & Credits"}
            </span>
          </div>
        }
        style={{ width: "980px", maxWidth: "96vw" }}
        modal
        draggable={false}
        dismissableMask
      >
        {running && (
          <div className="alert alert-warning d-flex align-items-center py-2">
            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            Sync running in the background. This window updates when it finishes.
          </div>
        )}
        {job?.status === "failed" && <div className="alert alert-danger py-2">{job.error}</div>}
        {result.zohoCheck && <div className="alert alert-warning py-2 small">{result.zohoCheck} Rows below may already exist in Zoho.</div>}

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Stat label={isPreview ? "Ready to sync" : "Still to sync"} value={groups.pending.length} tone="text-info" />
          <Stat label="Synced" value={groups.synced.length} tone="text-success" />
          <Stat label="Failed" value={groups.failed.length} tone={groups.failed.length ? "text-danger" : ""} />
          <Stat label="Skipped" value={groups.skipped.length} tone={groups.skipped.length ? "text-warning" : ""} />
          <Stat label="Value to sync (KSh)" value={money(pendingAmount)} />
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <ul className="nav nav-pills gap-1">
            {tabs.map((t) => (
              <li className="nav-item" key={t.id}>
                <button className={`nav-link py-1 px-3 ${tab === t.id ? "active" : "bg-light text-dark"}`} onClick={() => setTab(t.id)}>
                  {t.label}
                  <span className={`badge ms-2 ${tab === t.id ? "bg-white text-dark" : t.danger && t.count ? "bg-danger" : t.warn && t.count ? "bg-warning text-dark" : "bg-secondary"}`}>{t.count}</span>
                </button>
              </li>
            ))}
          </ul>
          <input className="form-control form-control-sm" style={{ maxWidth: 240 }} placeholder="Search account, reference..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="table-responsive border rounded-3" style={{ maxHeight: 420, overflowY: "auto" }}>
          {tab === "skipped" ? (
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                <tr><th>Type</th><th>Reference</th><th>Account</th><th className="text-end">Amount</th><th>Why it was skipped</th><th className="text-end">Action</th></tr>
              </thead>
              <tbody>
                {visible.map((r, idx) => (
                  <tr key={idx}>
                    <td><span className="badge bg-warning-subtle text-warning">{r.kind}</span></td>
                    <td className="text-nowrap">{r.ref || "—"}</td>
                    <td>{r.accountNumber}</td>
                    <td className="text-end">{money(r.amount)}</td>
                    <td className="small">{r.reason}</td>
                    <td className="text-end">
                      {r.invoiceNumber && (
                        <button className="btn btn-sm btn-outline-warning" disabled={!!rowBusy[r.invoiceNumber] || running} onClick={() => syncOne(r, { force: true })} title="Create the invoice and payment in Zoho even though the billing service did not credit it">
                          {rowBusy[r.invoiceNumber] ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="ti ti-refresh me-1"></i>Sync anyway</>}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!visible.length && <tr><td colSpan={6} className="text-center text-muted py-4">Nothing skipped</td></tr>}
              </tbody>
            </table>
          ) : (
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                <tr>
                  <th>Month</th><th>Account</th><th>Invoice</th><th className="text-end">Amount</th><th>Status</th><th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.slice(0, 500).map((i) => {
                  const st = STATUS[i.status] || { label: i.status, cls: "bg-light text-dark" };
                  const canSync = i.status === "dry_run" || i.status === "failed";
                  const canResync = i.status === "created" || i.status === "already_synced";
                  return (
                    <tr key={i.invoiceNumber || `${i.accountNumber}-${i.month}`}>
                      <td>{i.month}</td>
                      <td>{i.accountNumber}</td>
                      <td className="text-nowrap">{i.invoiceNumber || i.reference || "—"}</td>
                      <td className="text-end">{money(i.amount ?? i.debited)}</td>
                      <td>
                        <span className={`badge ${st.cls}`}>{st.label}</span>
                        {i.error && <div className="small text-danger mt-1" style={{ maxWidth: 280 }}>{i.error}</div>}
                        {i.zohoInvoiceId && (
                          <div className="small mt-1">
                            <a href={`https://books.zoho.com/app/${i.zohoOrganizationId}#/invoices/${i.zohoInvoiceId}`} target="_blank" rel="noreferrer">
                              <i className="ti ti-external-link me-1"></i>Open in Zoho
                            </a>
                            <span className="text-muted ms-2">{i.zohoStatus}{i.zohoDate ? ` · ${i.zohoDate}` : ""}{i.zohoBalance !== undefined ? ` · balance ${money(i.zohoBalance)}` : ""} · org {i.zohoOrganizationId}</span>
                          </div>
                        )}
                        {i.customerName && <div className="small text-muted mt-1">{i.customerCreated ? "New customer created in Zoho" : "Existing Zoho customer"}: {i.customerName}</div>}
                        {i.note && <div className="small text-warning mt-1" style={{ maxWidth: 280 }}>{i.note}</div>}
                      </td>
                      <td className="text-end">
                        {canResync && (
                          <button className="btn btn-sm btn-outline-secondary" disabled={!!rowBusy[i.invoiceNumber] || running} onClick={() => syncOne(i)} title="Check Zoho again and record anything that is missing">
                            {rowBusy[i.invoiceNumber] ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="ti ti-rotate me-1"></i>Resync</>}
                          </button>
                        )}
                        {canSync && (
                          <button className="btn btn-sm btn-outline-primary" disabled={!!rowBusy[i.invoiceNumber] || running} onClick={() => syncOne(i)}>
                            {rowBusy[i.invoiceNumber] ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="ti ti-refresh me-1"></i>{i.status === "failed" ? "Retry" : "Sync"}</>}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!visible.length && <tr><td colSpan={6} className="text-center text-muted py-4">{search ? "No matches" : "Nothing here"}</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        {visible.length > 500 && <div className="small text-muted mt-1">Showing the first 500 rows. Narrow the month range or search to see the rest.</div>}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">Sync a single row, Resync one that is already in Zoho, or sync everything that is ready.</small>
          <div className="d-flex gap-2">
            <button className="btn btn-light" onClick={() => setOpen(false)}>Close</button>
            <button className="btn btn-primary" disabled={!pendingCount || busy || running} onClick={syncAll}>
              <i className="ti ti-cloud-upload me-1"></i>Sync all ({pendingCount})
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ZohoUtilitySync;
